import React, { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import tr from 'date-fns/locale/tr';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import { Modal, TextField, Button, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { parseFlexible, toIsoUtcString, formatTrDateOnly } from '../utils/hopsDate';

registerLocale('tr', tr);

const StyledModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ModalContent = styled(Box)({
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '24px',
  width: '90%',
  maxWidth: '500px',
  maxHeight: '90vh',
  overflow: 'auto',
});

const FormContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  '& .react-datepicker-popper': {
    zIndex: 1400,
  },
});

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '24px',
});

// Özel TextField stilleri
const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#e2e8f0',
    },
    '&:hover fieldset': {
      borderColor: '#cbd5e0',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4299e1',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#4a5568',
    '&.Mui-focused': {
      color: '#4299e1',
    },
  },
});

// Select için özel stil
const customSelectStyles = {
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  control: (base) => ({
    ...base,
    minHeight: '40px',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#cbd5e0',
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    marginTop: '4px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#ebf8ff' : 'white',
    color: '#2d3748',
    padding: '8px 12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f7fafc',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#a0aec0',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#2d3748',
  }),
  input: (base) => ({
    ...base,
    color: '#2d3748',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#a0aec0',
    '&:hover': {
      color: '#718096',
    },
  }),
};

// DatePicker için özel wrapper component
const DatePickerTextField = styled(({ value, onClick, ...props }) => (
  <StyledTextField
    onClick={onClick}
    value={value}
    InputProps={{
      readOnly: true,
    }}
    {...props}
  />
))({});

const EditCariTransactionModal = ({
  isOpen,
  onClose,
  transaction,
  onSave,
  paymentMethodOptions,
}) => {
  const [editedTransaction, setEditedTransaction] = useState(null);

  // Transaction prop'u değiştiğinde state'i güncelle
  useEffect(() => {
    if (transaction) {
      setEditedTransaction({ ...transaction });
    }
  }, [transaction]);

  if (!transaction || !editedTransaction) return null;

  const formatDate = (date) => formatTrDateOnly(date);

  const handleInputChange = (field, value) => {
    setEditedTransaction(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(editedTransaction);
  };

  return (
    <StyledModal
      open={isOpen}
      onClose={onClose}
    >
      <ModalContent>
        <h2 style={{ margin: '0 0 24px 0', textAlign: 'center' }}>İşlem Düzenle</h2>
        
        <FormContainer>
          <StyledTextField
            select
            label="İşlem Tipi"
            value={editedTransaction.transactionType}
            onChange={(e) => handleInputChange('transactionType', e.target.value)}
            SelectProps={{
              native: true,
            }}
            fullWidth
            variant="outlined"
            disabled
          >
            <option value="Borç">Borç</option>
            <option value="Alacak">Alacak</option>
          </StyledTextField>

          {editedTransaction.transactionType === 'Alacak' && (
            <StyledTextField
              select
              label="Ödeme Şekli"
              value={editedTransaction.paymentDescription || ''}
              onChange={(e) => handleInputChange('paymentDescription', e.target.value)}
              SelectProps={{
                native: true,
              }}
              fullWidth
              variant="outlined"
            >
              <option value="">Ödeme Şekli Seçin</option>
              {paymentMethodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledTextField>
          )}

          <StyledTextField
            label="Tutar"
            type="number"
            value={editedTransaction.amount || ''}
            onChange={(e) => handleInputChange('amount', e.target.value)}
            fullWidth
            variant="outlined"
          />

          <DatePicker
            selected={editedTransaction.date ? (parseFlexible(editedTransaction.date) || new Date()) : new Date()}
            onChange={(date) => handleInputChange('date', toIsoUtcString(date))}
            dateFormat="dd.MM.yyyy"
            locale="tr"
            customInput={
              <DatePickerTextField
                label="Tarih"
                fullWidth
                variant="outlined"
                value={formatDate(editedTransaction.date)}
              />
            }
          />

          <StyledTextField
            label="Açıklama"
            multiline
            rows={4}
            value={editedTransaction.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            variant="outlined"
          />

          <ButtonContainer>
            <Button
              variant="outlined"
              onClick={onClose}
              style={{ minWidth: '120px' }}
            >
              İptal
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              style={{ minWidth: '120px' }}
              color="primary"
            >
              Kaydet
            </Button>
          </ButtonContainer>
        </FormContainer>
      </ModalContent>
    </StyledModal>
  );
};

export default EditCariTransactionModal; 